import {
  DIGITAL_LOAN_STEP_ORDER,
  DIGITAL_LOAN_STEPS,
} from 'constants/digitalLoan';

import { isPhase4FeStep } from './phase4Routing';

const PHASE2_CATALOG_STEPS = [
  DIGITAL_LOAN_STEPS.GUARANTEE_FAMILY,
  DIGITAL_LOAN_STEPS.GUARANTEE_SUBFAMILY,
  DIGITAL_LOAN_STEPS.GUARANTEE_ARTICLE,
  DIGITAL_LOAN_STEPS.GUARANTEE_BRAND,
  DIGITAL_LOAN_STEPS.GUARANTEE_MODEL,
  DIGITAL_LOAN_STEPS.PHOTO_REQUIREMENTS,
  DIGITAL_LOAN_STEPS.PHOTO_CAPTURE,
  DIGITAL_LOAN_STEPS.PHOTO_REVIEW,
];

const PHASE2_RESULT_STEPS = [
  DIGITAL_LOAN_STEPS.VALIDATION_FAILED,
  DIGITAL_LOAN_STEPS.INSUFFICIENT_VALUE,
  DIGITAL_LOAN_STEPS.MISSING_ONBOARDING,
  DIGITAL_LOAN_STEPS.NOT_PREAPPROVED,
  DIGITAL_LOAN_STEPS.PRE_OFFER,
];

const PHASE3_RESUME_STEPS = [
  DIGITAL_LOAN_STEPS.MISSING_ONBOARDING,
  DIGITAL_LOAN_STEPS.PRE_OFFER,
  DIGITAL_LOAN_STEPS.VISIT_STATUS,
  DIGITAL_LOAN_STEPS.NOT_PREAPPROVED,
  DIGITAL_LOAN_STEPS.INSUFFICIENT_VALUE,
  DIGITAL_LOAN_STEPS.VALIDATION_FAILED,
];

function getMaxAllowedStepIndex(progress) {
  let maxIndex = DIGITAL_LOAN_STEP_ORDER.indexOf(DIGITAL_LOAN_STEPS.INTRO);

  if (progress.introCompleted) {
    maxIndex = DIGITAL_LOAN_STEP_ORDER.indexOf(DIGITAL_LOAN_STEPS.PERMISSIONS);
  }
  if (progress.applicationStarted && progress.blacklistPassed) {
    maxIndex = DIGITAL_LOAN_STEP_ORDER.indexOf(DIGITAL_LOAN_STEPS.LOCATION);
  }
  if (progress.coordinatesValidated) {
    maxIndex = DIGITAL_LOAN_STEP_ORDER.indexOf(DIGITAL_LOAN_STEPS.APPLICABILITY_LOAN);
  }
  if (progress.loanStepCompleted) {
    maxIndex = DIGITAL_LOAN_STEP_ORDER.indexOf(DIGITAL_LOAN_STEPS.APPLICABILITY_ADDRESS);
  }
  if (progress.addressStepCompleted) {
    maxIndex = DIGITAL_LOAN_STEP_ORDER.indexOf(DIGITAL_LOAN_STEPS.APPLICABILITY_IDENTITY);
  }
  if (progress.questionnaireCompleted) {
    maxIndex = DIGITAL_LOAN_STEP_ORDER.indexOf(
      DIGITAL_LOAN_STEPS.GUARANTEE_AVAILABILITY,
    );
  }
  if (progress.guaranteeFlowUnlocked) {
    maxIndex = DIGITAL_LOAN_STEP_ORDER.indexOf(DIGITAL_LOAN_STEPS.VISIT_STATUS);
  }
  if (progress.phase3Resume) {
    maxIndex = Math.max(
      maxIndex,
      DIGITAL_LOAN_STEP_ORDER.indexOf(DIGITAL_LOAN_STEPS.VISIT_STATUS),
    );
  }
  if (progress.isModifyingTerm) {
    maxIndex = Math.max(
      maxIndex,
      DIGITAL_LOAN_STEP_ORDER.indexOf(DIGITAL_LOAN_STEPS.APPLICABILITY_LOAN),
    );
  }

  return maxIndex;
}

export function getPermittedStep(step, progress) {
  if (!DIGITAL_LOAN_STEP_ORDER.includes(step)) {
    return DIGITAL_LOAN_STEPS.INTRO;
  }

  if (progress.phase4Resume && isPhase4FeStep(step)) {
    return step;
  }

  if (progress.phase3Resume && PHASE3_RESUME_STEPS.includes(step)) {
    return step;
  }

  if (step === DIGITAL_LOAN_STEPS.PERMISSIONS && !progress.introCompleted) {
    return DIGITAL_LOAN_STEPS.INTRO;
  }

  if (step === DIGITAL_LOAN_STEPS.LOCATION && !progress.blacklistPassed) {
    if (progress.introCompleted) {
      return DIGITAL_LOAN_STEPS.PERMISSIONS;
    }
    return DIGITAL_LOAN_STEPS.INTRO;
  }

  if (
    progress.isModifyingTerm
    && step === DIGITAL_LOAN_STEPS.APPLICABILITY_LOAN
  ) {
    return DIGITAL_LOAN_STEPS.APPLICABILITY_LOAN;
  }

  if (
    !progress.questionnaireCompleted
    && !progress.phase3Resume
    && (
      step === DIGITAL_LOAN_STEPS.GUARANTEE_AVAILABILITY
      || PHASE2_CATALOG_STEPS.includes(step)
      || PHASE2_RESULT_STEPS.includes(step)
    )
  ) {
    return getPermittedStep(
      DIGITAL_LOAN_STEPS.APPLICABILITY_IDENTITY,
      progress,
    );
  }

  if (
    progress.questionnaireCompleted
    && !progress.guaranteeFlowUnlocked
    && !progress.phase3Resume
    && (
      PHASE2_CATALOG_STEPS.includes(step)
      || PHASE2_RESULT_STEPS.includes(step)
    )
  ) {
    return DIGITAL_LOAN_STEPS.GUARANTEE_AVAILABILITY;
  }

  const requestedIndex = DIGITAL_LOAN_STEP_ORDER.indexOf(step);
  const maxIndex = getMaxAllowedStepIndex(progress);

  if (requestedIndex > maxIndex) {
    return DIGITAL_LOAN_STEP_ORDER[maxIndex];
  }

  return step;
}

export function getPreviousStep(step) {
  if (PHASE2_RESULT_STEPS.includes(step)) {
    return DIGITAL_LOAN_STEPS.PHOTO_REVIEW;
  }

  if (step === DIGITAL_LOAN_STEPS.GUARANTEE_AVAILABILITY) {
    return DIGITAL_LOAN_STEPS.APPLICABILITY_IDENTITY;
  }

  if (step === DIGITAL_LOAN_STEPS.GUARANTEE_FAMILY) {
    return DIGITAL_LOAN_STEPS.GUARANTEE_AVAILABILITY;
  }

  if (step === DIGITAL_LOAN_STEPS.PHOTO_REQUIREMENTS) {
    return DIGITAL_LOAN_STEPS.GUARANTEE_MODEL;
  }

  const index = DIGITAL_LOAN_STEP_ORDER.indexOf(step);
  if (index <= 0) return DIGITAL_LOAN_STEPS.INTRO;

  return DIGITAL_LOAN_STEP_ORDER[index - 1];
}

export function isPhase4Step(step) {
  return isPhase4FeStep(step);
}

export function isPhase3Step(step) {
  return (
    step === DIGITAL_LOAN_STEPS.PRE_OFFER
    || step === DIGITAL_LOAN_STEPS.VISIT_STATUS
    || step === DIGITAL_LOAN_STEPS.MISSING_ONBOARDING
  );
}

export function isPhase2Step(step) {
  return (
    step === DIGITAL_LOAN_STEPS.GUARANTEE_AVAILABILITY
    || PHASE2_CATALOG_STEPS.includes(step)
    || PHASE2_RESULT_STEPS.includes(step)
  );
}
